/** Planificación de control on/off para el asistente (PIN/cámara → WebSocket). */

export function isDevicePowerOn(estado: unknown): boolean {
  return (
    estado === true ||
    estado === 'on' ||
    estado === 1 ||
    String(estado).toLowerCase() === 'true'
  );
}

export type PowerNoopReason = 'already_on' | 'already_off';

export interface PowerControlPlan {
  kind: 'noop' | 'set';
  targetState?: boolean;
  noopReason?: PowerNoopReason;
}

/** Resuelve estado objetivo según acción MCP (encender/apagar/alternar). */
export function planPowerControl(
  device: { estado?: unknown },
  pendingAction: string,
  pendingValue: string | null | undefined,
  controlAction?: string
): PowerControlPlan {
  let desired: boolean | 'toggle' = 'toggle';

  if (pendingAction === 'set_power') {
    const pv = pendingValue == null ? '' : String(pendingValue).toLowerCase();
    desired = pv === 'on' || pv === 'true' || pv === '1';
  } else {
    const ca = (controlAction || '').toLowerCase();
    if (ca === 'encender') desired = true;
    else if (ca === 'apagar') desired = false;
    else if (ca === 'alternar') desired = 'toggle';
  }

  const isOn = isDevicePowerOn(device.estado);

  if (desired === 'toggle') {
    return { kind: 'set', targetState: !isOn };
  }

  if (isOn === desired) {
    return {
      kind: 'noop',
      noopReason: desired ? 'already_on' : 'already_off',
    };
  }

  return { kind: 'set', targetState: desired };
}

export type AssistantLang = 'es' | 'en';

export function formatAssistantControlNoop(
  deviceName: string,
  reason: PowerNoopReason,
  lang: AssistantLang = 'es'
): string {
  if (lang === 'en') {
    if (reason === 'already_on') {
      return `«${deviceName}» was already on; nothing to do.`;
    }
    return `«${deviceName}» was already off; nothing to do.`;
  }
  if (reason === 'already_on') {
    return `«${deviceName}» ya estaba encendido; no hace falta hacer nada.`;
  }
  return `«${deviceName}» ya estaba apagado; no hace falta hacer nada.`;
}

/** Tipos que exponen nivel (brillo/apertura) en la tarjeta del asistente. */
const REGULABLE_TIPOS = new Set(['DIMMER2', 'PLUS2PM']);

export interface ControlResultDevice {
  tipo?: string | null;
  /** Etiqueta de artefacto para el icono (p. ej. «Luz», «Cortina»). */
  artifact?: string | null;
  /** Brillo retenido del DIMMER2 (0..100), independiente de on/off. */
  brightness?: number | null;
  /** Apertura de cortina PLUS2PM (0..100). */
  coverPosition?: number | null;
  /** Potencia instantánea en vatios. */
  consumoW?: number | null;
}

function clampPct(raw: number | null | undefined): number | undefined {
  if (raw === null || raw === undefined || Number.isNaN(raw)) return undefined;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/** Mismo formato de W que el backend (`_format_consumo_w_segment`). */
function formatControlW(watts: number | null | undefined): string {
  const w = typeof watts === 'number' && Number.isFinite(watts) ? watts : 0;
  if (w >= 10) return `${Math.round(w)} W`;
  if (w > 0) return `${w.toFixed(1)} W`;
  return '0 W';
}

/** Nivel a mostrar (brillo o apertura) si el dispositivo es regulable. */
function controlResultLevel(device?: ControlResultDevice): number | undefined {
  if (!device) return undefined;
  const tipo = (device.tipo || '').toUpperCase();
  if (!REGULABLE_TIPOS.has(tipo)) return undefined;
  if (tipo === 'PLUS2PM') return clampPct(device.coverPosition);
  return clampPct(device.brightness);
}

export function formatAssistantControlSuccess(
  deviceName: string,
  controlAction: string | undefined,
  targetState: boolean,
  extras: { requires_pin?: boolean; has_cameras?: boolean },
  lang: AssistantLang = 'es',
  roomName?: string,
  device?: ControlResultDevice
): string {
  void roomName;
  const parts: string[] = [];
  if (extras.requires_pin) {
    parts.push(lang === 'en' ? 'PIN verified' : 'PIN verificado');
  }
  if (extras.has_cameras) {
    parts.push(lang === 'en' ? 'camera' : 'cámara');
  }
  const extra = parts.length > 0 ? ` (${parts.join(', ')})` : '';
  const ca = (controlAction || '').toLowerCase();
  const on = targetState;
  let header: string;
  if (lang === 'en') {
    if (ca === 'encender') header = 'Turned on';
    else if (ca === 'apagar') header = 'Turned off';
    else header = on ? 'Turned on' : 'Turned off';
  } else if (ca === 'encender') {
    header = 'Encendí';
  } else if (ca === 'apagar') {
    header = 'Apagué';
  } else {
    header = on ? 'Encendí' : 'Apagué';
  }
  const title = `${header}${extra}`;

  // Línea unificada idéntica al backend: • nombre [(artefacto)] · on/off [· NN%] · WW W
  const label = device?.artifact
    ? `${deviceName} (${device.artifact})`
    : deviceName;
  const lineParts: string[] = [label, on ? 'on' : 'off'];
  const level = controlResultLevel(device);
  if (level !== undefined) {
    lineParts.push(`${level}%`);
  }
  lineParts.push(formatControlW(device?.consumoW));
  const line = `• ${lineParts.join(' · ')}`;
  return `## ${title}\n${line}`;
}

export function previewAssistantDeviceControl(
  device: { estado?: unknown; nombre?: string },
  deferred: {
    pending_action: string;
    pending_action_value?: string | null;
    control_action?: string;
    device_name: string;
    idioma?: string;
  }
): { noop: true; message: string } | { noop: false } {
  const plan = planPowerControl(
    device,
    deferred.pending_action,
    deferred.pending_action_value,
    deferred.control_action
  );
  if (plan.kind === 'noop' && plan.noopReason) {
    const lang: AssistantLang =
      deferred.idioma === 'en' ? 'en' : 'es';
    return {
      noop: true,
      message: formatAssistantControlNoop(
        device.nombre || deferred.device_name,
        plan.noopReason,
        lang
      ),
    };
  }
  return { noop: false };
}
